import { Flex, Group, IconFont, Modal, SegmentedControl, Text, Tooltip } from '@byecode/ui'
import { ListItem4ByecodeUi, TagIcon, useAtomAction, useAtomData } from '@lighthouse/shared'
import * as React from 'react'
import { useCallback } from 'react'

import { updateAppVersionConfigAtom } from '@/atoms/application/action'
import { applicationSettingAtom } from '@/atoms/application/state'
import { updatePageAtom, updatePageListAtom } from '@/atoms/page/action'
import { lastPageOfStackAtom, pageAtomFamily, pageInternalStateAtomFamily } from '@/atoms/page/state'
import { AsideType } from '@/atoms/page/types'

interface NavbarVisibilityProps {
    pageId: string
}

const SHOW_OPTIONS = [
    {
        label: '显示',
        value: '1'
    },
    {
        label: '隐藏',
        value: '0'
    }
]

const NavbarVisibility: React.FunctionComponent<NavbarVisibilityProps> = ({ pageId }) => {
    const stackId = useAtomData(
        lastPageOfStackAtom,
        useCallback(stack => stack?.stackId ?? '', [])
    )
    const [isShowPageNavbar, isShowPageTabBar] = useAtomData(
        pageAtomFamily(pageId),
        useCallback(s => [s?.isShowNavbar ?? false, s?.isShowTabBar ?? false], [])
    )
    const navbar = useAtomData(
        applicationSettingAtom,
        useCallback(s => s?.navbar, [])
    )

    const { run: updatePage } = useAtomAction(updatePageAtom)
    const { run: updateAppVersionConfig } = useAtomAction(updateAppVersionConfigAtom)
    const { run: updatePageList } = useAtomAction(updatePageListAtom)
    const { run: setPageInternalState } = useAtomAction(pageInternalStateAtomFamily(stackId))

    return (
        <Group label="全局导航栏">
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <div>顶部导航栏显隐</div>
                <Flex gap={8}>
                    <SegmentedControl
                        data={SHOW_OPTIONS}
                        value={Number(isShowPageNavbar).toString()}
                        onChange={v => {
                            if (navbar?.isUpdatePageShowNavbar) {
                                updatePage({ id: pageId, isShowNavbar: Boolean(Number(v)) })
                                return
                            }
                            updatePageList({ isShowNavbar: Boolean(Number(v)) })
                        }}
                    />
                    <Tooltip
                        title={
                            <p style={{ width: 200, lineHeight: '20px', fontSize: 12 }}>
                                全局设置开关：启用后，顶部导航栏的显隐设置影响所有页面；关闭后，可以为每个页面单独设置顶部导航栏的显隐配置
                            </p>
                        }
                    >
                        <div
                            onClick={async () => {
                                if (navbar?.isUpdatePageShowNavbar) {
                                    const res = await Modal.confirm({
                                        okStatus: 'primary',
                                        okText: '确定',
                                        title: '确定将顶部导航栏显隐设为全局配置？',
                                        content: `启用后，所有页面的顶部导航栏都将设为“${isShowPageNavbar ? '显示' : '隐藏'}”。`
                                    })
                                    if (res && navbar) {
                                        updateAppVersionConfig({
                                            config: { navbar: { ...navbar, isUpdatePageShowNavbar: false } }
                                        })
                                        updatePageList({ isShowNavbar: isShowPageNavbar })
                                    }

                                    return
                                }
                                navbar &&
                                    updateAppVersionConfig({
                                        config: { navbar: { ...navbar, isUpdatePageShowNavbar: true } }
                                    })
                            }}
                        >
                            {navbar?.isUpdatePageShowNavbar ? (
                                <TagIcon
                                    icon="LinkBreak"
                                    size={32}
                                    radius={6}
                                    enableHover
                                    background="var(--color-gray-100)"
                                    iconColor="var(--color-gray-400)"
                                    hoverColor="var(--color-gray-900)"
                                    hoverBackground="var(--color-gray-200)"
                                />
                            ) : (
                                <TagIcon
                                    icon="PropertyLink"
                                    size={32}
                                    radius={6}
                                    enableHover
                                    background="var(--color-theme-4)"
                                    hoverColor="var(--color-theme-7)"
                                    hoverBackground="var(--color-theme-5)"
                                    iconColor="var(--color-theme-7)"
                                />
                            )}
                        </div>
                    </Tooltip>
                </Flex>
            </ListItem4ByecodeUi>
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <div>底部导航栏显隐(手机端)</div>
                <Flex gap={8}>
                    <SegmentedControl
                        data={SHOW_OPTIONS}
                        value={Number(isShowPageTabBar).toString()}
                        onChange={v => {
                            if (navbar?.isUpdatePageShowTabBar) {
                                updatePage({ id: pageId, isShowTabBar: Boolean(Number(v)) })
                                return
                            }
                            updatePageList({ isShowTabBar: Boolean(Number(v)) })
                        }}
                    />
                    <Tooltip
                        title={
                            <p style={{ width: 200, lineHeight: '20px', fontSize: 12 }}>
                                全局设置开关：启用后，底部导航栏的显隐设置影响所有页面；关闭后，可以为每个页面单独设置底部导航栏的显隐配置
                            </p>
                        }
                    >
                        <div
                            onClick={async () => {
                                if (navbar?.isUpdatePageShowTabBar) {
                                    const res = await Modal.confirm({
                                        okStatus: 'primary',
                                        okText: '确定',
                                        title: '确定将底部导航栏显隐设为全局配置？',
                                        content: `启用后，所有页面的底部导航栏都将设为“${isShowPageTabBar ? '显示' : '隐藏'}”。`
                                    })
                                    if (res && navbar) {
                                        updateAppVersionConfig({
                                            config: { navbar: { ...navbar, isUpdatePageShowTabBar: false } }
                                        })
                                        updatePageList({ isShowTabBar: isShowPageTabBar })
                                    }

                                    return
                                }
                                navbar &&
                                    updateAppVersionConfig({
                                        config: { navbar: { ...navbar, isUpdatePageShowTabBar: true } }
                                    })
                            }}
                        >
                            {navbar?.isUpdatePageShowTabBar ? (
                                <TagIcon
                                    icon="LinkBreak"
                                    size={32}
                                    radius={6}
                                    enableHover
                                    background="var(--color-gray-100)"
                                    iconColor="var(--color-gray-400)"
                                    hoverColor="var(--color-gray-900)"
                                    hoverBackground="var(--color-gray-200)"
                                />
                            ) : (
                                <TagIcon
                                    icon="PropertyLink"
                                    size={32}
                                    radius={6}
                                    enableHover
                                    background="var(--color-theme-4)"
                                    hoverColor="var(--color-theme-7)"
                                    hoverBackground="var(--color-theme-5)"
                                    iconColor="var(--color-theme-7)"
                                />
                            )}
                        </div>
                    </Tooltip>
                </Flex>
            </ListItem4ByecodeUi>
            {isShowPageNavbar && (
                <ListItem4ByecodeUi
                    justifyContent="space-between"
                    alignItems="center"
                    style={{ cursor: 'pointer' }}
                    onClick={() => {
                        setPageInternalState(draft => {
                            draft.selectedNodes = undefined
                            draft.asideType = AsideType.NAVBAR
                        })
                    }}
                >
                    <div>配置导航栏</div>
                    <Flex>
                        <Text color="var(--color-gray-400)">点击前往</Text>
                        <IconFont size={16} type="ArrowRightSmall" color="var(--color-gray-400)" />
                    </Flex>
                </ListItem4ByecodeUi>
            )}
        </Group>
    )
}

export default NavbarVisibility
